import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { HKBadge } from "@/components/site/HKBadge";
import { SectionHeading } from "@/components/site/SectionHeading";
import { CTASection } from "@/components/site/CTASection";
import { ONBOARDING_STEPS, ENGAGEMENT_PRINCIPLES, BRAND } from "@/lib/content";
import { Seo } from "@/components/site/Seo";

export default function EngagementPage() {
  const { t } = useTranslation();
  const whoItems = t("content.engagementWho.items", { returnObjects: true }) || [];
  const whoNotItems = t("content.engagementWhoNot.items", { returnObjects: true }) || [];
  return (
    <div data-testid="engagement-page">
      <Seo
        title={t("seo.engagement.metaTitle")}
        description={t("seo.engagement.metaDescription")}
        keywords={t("seo.engagement.keywords")}
        path="/engagement"
        breadcrumbs={[
          { name: t("seo.breadcrumbHome"), url: "/" },
          { name: t("seo.engagement.breadcrumbLabel"), url: "/engagement" },
        ]}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dc-dot-grid opacity-50" aria-hidden />
        <div className="relative mx-auto w-full max-w-6xl px-4 pb-10 pt-14 sm:px-6 sm:pb-14 sm:pt-20 lg:px-8">
          <HKBadge />
          <h1 className="mt-5 max-w-4xl text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-[58px] lg:leading-[1.05]">{t("pages.engagementTitle")}</h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">{t("pages.engagementSubtitle")}</p>
          <p className="mt-3 text-xs font-bold uppercase tracking-[0.22em] text-blue-700">{BRAND.marketingLine}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/contact" data-testid="engagement-hero-cta"><Button className="h-11 rounded-lg bg-blue-600 px-5 text-white hover:bg-blue-700">{t("cta.primary")}<ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
            <Link to="/pricing"><Button variant="outline" className="h-11 rounded-lg border-slate-300 bg-white px-5 text-slate-900 hover:bg-slate-50">{t("cta.seePricing")}</Button></Link>
          </div>
        </div>
      </section>

      <section data-testid="engagement-principles-section" className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
        <SectionHeading eyebrow={t("sections.opPrinciples.eyebrow")} title={t("sections.opPrinciples.title")} description={t("sections.opPrinciples.desc")} testIdPrefix="engagement-principles" />
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {ENGAGEMENT_PRINCIPLES.map((p, idx) => (
            <motion.div key={p.key} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.3, delay: idx * 0.04 }} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`engagement-principle-${idx}`}>
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-50 text-blue-700"><p.icon className="h-5 w-5" /></div>
              <h3 className="mt-4 text-base font-bold text-slate-900">{t(`content.${p.tk}.title`)}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{t(`content.${p.tk}.desc`)}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50" data-testid="engagement-phases-section">
        <div className="mx-auto w-full max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <SectionHeading eyebrow={t("sections.engagementHow.eyebrow")} title={t("sections.engagementHow.title")} description={t("sections.engagementHow.desc")} testIdPrefix="engagement-phases" />
          <ol className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {ONBOARDING_STEPS.map((ph, idx) => (
              <motion.li key={ph.step} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.3, delay: idx * 0.04 }} className="relative rounded-2xl border border-slate-200 bg-white p-6 sm:p-7" data-testid={`engagement-phase-${idx}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">{t("common.stepLabel")} {ph.step}</div>
                  <div className="grid h-9 w-9 place-items-center rounded-lg bg-blue-50 text-blue-700"><ph.icon className="h-5 w-5" /></div>
                </div>
                <h3 className="mt-3 text-lg font-extrabold text-slate-900">{t(`content.${ph.tk}.title`)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{t(`content.${ph.tk}.desc`)}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      <section data-testid="engagement-fit-section" className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6 sm:p-8">
            <h3 className="text-lg font-extrabold text-slate-900">{t("content.engagementWho.title")}</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {whoItems.map((x) => (<li key={x} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" /> {x}</li>))}
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
            <h3 className="text-lg font-extrabold text-slate-900">{t("content.engagementWhoNot.title")}</h3>
            <p className="mt-2 text-sm text-slate-600">{t("content.engagementWhoNot.lead")}</p>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {whoNotItems.map((x) => (<li key={x} className="flex items-start gap-2"><span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-red-500" /> {x}</li>))}
            </ul>
          </div>
        </div>
      </section>

      <CTASection title={t("ctaSection.engagementTitle")} primaryLabel={t("cta.primary")} />
    </div>
  );
}
